import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RentalDashboard } from '../components/rentals/RentalDashboard';
import { RentalList } from '../components/rentals/RentalList';
import { RentalForm } from '../components/rentals/RentalForm';
import { MachineStatusList } from '../components/rentals/MachineStatusList';
import { RefillForm } from '../components/rentals/RefillForm';
import { RentalContractDetail } from '../components/rentals/RentalContractDetail';
import { rentalService } from '../services/rentalService';
import { refillService } from '../services/refillService';
import type { CreateRentalContractData, CreateRefillVisitData } from '../types/rental';

type PageMode = 'dashboard' | 'list' | 'create' | 'machines' | 'refill' | 'detail' | 'edit';

export const RentalsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<PageMode>('dashboard');
  const [editingContractId, setEditingContractId] = useState<string | null>(null);

  // Sync mode with URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/new')) {
      setMode('create');
      setEditingContractId(null);
    } else if (path.includes('/list')) {
      setMode('list');
      setEditingContractId(null);
    } else if (path.includes('/machines')) {
      setMode('machines');
      setEditingContractId(null);
    } else if (path.includes('/refill')) {
      setMode('refill');
      setEditingContractId(null);
    } else if (path.match(/\/rentals\/([a-zA-Z0-9]+)\/edit$/)) {
      const match = path.match(/\/rentals\/([a-zA-Z0-9]+)\/edit$/);
      if (match) {
        setMode('edit');
        setEditingContractId(match[1]);
      }
    } else if (path.match(/\/rentals\/[a-zA-Z0-9]+$/)) {
      setMode('detail');
      setEditingContractId(null);
    } else if (path === '/rentals') {
      setMode('dashboard');
      setEditingContractId(null);
    }
  }, [location.pathname]);

  const queryClient = useQueryClient();

  // Fetch contract data when editing
  const { data: editingContract, isLoading: isLoadingContract } = useQuery({
    queryKey: ['rental-contract', editingContractId],
    queryFn: () => rentalService.getRentalContract(editingContractId!),
    enabled: mode === 'edit' && !!editingContractId
  });

  const createContractMutation = useMutation({
    mutationFn: rentalService.createRentalContract,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['rental-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['available-machines'] });
      navigate('/rentals/list');
      alert('Rental contract created successfully!');
    },
    onError: (error: any) => {
      console.error('Failed to create rental contract:', error);
      alert(`Failed to create contract: ${error.response?.data?.message || error.message}`);
    }
  });

  const updateContractMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateRentalContractData }) =>
      rentalService.updateRentalContract(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['rental-contract', editingContractId] });
      queryClient.invalidateQueries({ queryKey: ['rental-dashboard-stats'] });
      navigate('/rentals/list');
      alert('Rental contract updated successfully!');
    },
    onError: (error: any) => {
      console.error('Failed to update rental contract:', error);
      alert(`Failed to update contract: ${error.response?.data?.message || error.message}`);
    }
  });

  const createRefillMutation = useMutation({
    mutationFn: refillService.createRefillVisit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refill-visits'] });
      queryClient.invalidateQueries({ queryKey: ['rental-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['machines-needing-refill'] });
      navigate('/rentals');
      alert('Refill visit recorded successfully!');
    },
    onError: (error: any) => {
      console.error('Failed to record refill visit:', error);
      alert(`Failed to record refill: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleCreateContract = (data: CreateRentalContractData) => {
    createContractMutation.mutate(data);
  };

  const handleUpdateContract = (data: CreateRentalContractData) => {
    if (editingContractId) {
      updateContractMutation.mutate({ id: editingContractId, data });
    }
  };

  const handleCreateRefill = (data: CreateRefillVisitData) => {
    createRefillMutation.mutate(data);
  };

  const navTabs: { id: PageMode; label: string; path: string; active: boolean }[] = [
    { id: 'dashboard', label: 'Dashboard', path: '/rentals', active: mode === 'dashboard' },
    { id: 'list', label: 'Contracts', path: '/rentals/list', active: mode === 'list' || mode === 'create' },
    { id: 'machines', label: 'Machine Status', path: '/rentals/machines', active: mode === 'machines' },
    { id: 'refill', label: 'Record Refill', path: '/rentals/refill', active: mode === 'refill' },
  ];

  const renderContent = () => {
    switch (mode) {
      case 'dashboard':
        return <RentalDashboard />;

      case 'list':
        return <RentalList />;

      case 'create':
        return (
          <RentalForm
            onSubmit={handleCreateContract}
            onCancel={() => navigate('/rentals/list')}
          />
        );

      case 'edit':
        if (isLoadingContract) {
          return (
            <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    style={{ height: 20, background: 'rgba(241,245,249,1)', borderRadius: 3, width: i === 1 ? '33%' : '100%' }}
                  />
                ))}
              </div>
            </div>
          );
        }
        if (!editingContract) {
          return (
            <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 24 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c0504a', margin: 0 }}>Contract not found</p>
            </div>
          );
        }
        return (
          <RentalForm
            contract={editingContract}
            onSubmit={handleUpdateContract}
            onCancel={() => navigate('/rentals/list')}
          />
        );

      case 'machines':
        return <MachineStatusList />;

      case 'refill':
        return (
          <RefillForm
            onSubmit={handleCreateRefill}
            onCancel={() => navigate('/rentals')}
          />
        );

      case 'detail':
        return <RentalContractDetail />;

      default:
        return <RentalDashboard />;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page Header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
          Operations
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
          Rentals
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
          Manage rental contracts, machine status, and refill visits
        </div>
      </div>

      {/* Navigation Tabs - Hide when viewing contract detail */}
      {mode !== 'detail' && (
        <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', overflowX: 'auto' }}>
            {navTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                style={{
                  padding: '14px 24px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  fontWeight: 400,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  border: 'none',
                  borderBottom: tab.active ? '2px solid #d97706' : '2px solid transparent',
                  background: 'transparent',
                  color: tab.active ? '#d97706' : '#8a8778',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => { if (!tab.active) e.currentTarget.style.color = '#5b5a52'; }}
                onMouseLeave={(e) => { if (!tab.active) e.currentTarget.style.color = '#8a8778'; }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {renderContent()}
    </div>
  );
};
